/**
 * PreviewToProductionRedirect
 * ───────────────────────────────────────────────────────────────────────────
 * CORRECTIF OPS — pas un composant métier.
 *
 * Contexte : les QR codes émis avant le fix env-aware (avant 2026-04-26)
 * embarquent dans leur PNG l'URL Preview. Quand Preview dort, ils ne sont
 * plus vérifiables. Comme l'URL est encodée dans le pixel du QR, on ne peut
 * pas la modifier rétroactivement.
 *
 * Mitigation : si la page courante est servie sur le domaine Preview ET que
 * le pathname correspond à une route publique de vérification, on redirige
 * immédiatement vers le domaine Production en conservant le pathname, query
 * et hash. Les nouveaux QR (post-D10) pointent déjà vers le bon domaine et
 * ne déclenchent jamais ce redirect.
 *
 * Hostname Preview détecté : *.preview.emergentagent.com
 * Hostname Production cible : configurable via REACT_APP_PRODUCTION_URL
 *   (fallback: https://www.objectifscopoutremer.com).
 *
 * Modes d'utilisation :
 *  1. <PreviewToProductionRedirect><Page /></PreviewToProductionRedirect>
 *     → Redirige inconditionnellement (route déjà connue comme publique).
 *  2. <Routes>... <PreviewRedirectGuard /> ...</Routes> en haut
 *     → Détecte automatiquement les pathnames publics de vérification.
 *
 * Aucune dépendance backend — la redirection est purement côté navigateur.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const PRODUCTION_URL = (
  process.env.REACT_APP_PRODUCTION_URL || 'https://www.objectifscopoutremer.com'
).replace(/\/$/, '');

const isPreviewHost = (hostname) =>
  /\.preview\.emergentagent\.com$/i.test(hostname || '');

// Pathnames publics de vérification : on redirige depuis Preview vers Prod.
// On accepte les variantes /verify/* (anglais) et /verifier/* (français),
// avec ou sans /api/ prefix (anciens QR codes).
const PUBLIC_VERIFICATION_PATTERNS = [
  /^\/verify(\/|$)/i,
  /^\/verifier(\/|$)/i,
  /^\/dos\/verify(\/|$)/i,
  /^\/api\/verifier(\/|$)/i,
  /^\/api\/attestation\/verifier(\/|$)/i,
  /^\/api\/conditions-generales\/verifier(\/|$)/i,
  /^\/api\/conditions-particulieres\/verifier(\/|$)/i,
  /^\/api\/proposition\/verifier(\/|$)/i,
  /^\/verify-document(\/|$)/i,
];

const isPublicVerificationPath = (pathname) =>
  PUBLIC_VERIFICATION_PATTERNS.some((pattern) => pattern.test(pathname || ''));

const RedirectingScreen = () => (
  <div
    data-testid="qr-preview-redirect-banner"
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0f172a',
      color: '#f8fafc',
      fontFamily: 'system-ui, sans-serif',
      padding: '2rem',
      textAlign: 'center',
    }}
  >
    <div style={{ fontSize: '0.9rem', opacity: 0.7, marginBottom: '0.5rem' }}>
      Vérification de document
    </div>
    <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
      Redirection vers la plateforme officielle…
    </h1>
    <p style={{ maxWidth: '32rem', opacity: 0.85 }}>
      Vous accédez à un document via un environnement temporaire. Vous êtes
      automatiquement redirigé vers&nbsp;
      <a
        href={`${PRODUCTION_URL}${typeof window !== 'undefined' ? window.location.pathname : ''}`}
        style={{ color: '#60a5fa', textDecoration: 'underline' }}
        data-testid="qr-preview-redirect-link"
      >
        {PRODUCTION_URL.replace(/^https?:\/\//, '')}
      </a>.
    </p>
    <p style={{ marginTop: '2rem', fontSize: '0.85rem', opacity: 0.6 }}>
      Si la redirection ne s'effectue pas automatiquement, cliquez sur le lien ci-dessus.
    </p>
  </div>
);

/**
 * Wrapper inconditionnel : redirige toute route encapsulée si servie sur Preview.
 * Conserve la rétro-compatibilité avec l'utilisation initiale sur des routes
 * connues (ex. <Route element={<PreviewToProductionRedirect>...}/>).
 */
const PreviewToProductionRedirect = ({ children }) => {
  const [shouldRedirect] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isPreviewHost(window.location.hostname);
  });

  useEffect(() => {
    if (!shouldRedirect) return;
    const target = `${PRODUCTION_URL}${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(target);
  }, [shouldRedirect]);

  if (shouldRedirect) return <RedirectingScreen />;
  return children;
};

/**
 * Garde globale à monter en haut de <Routes> (idéalement à l'intérieur de
 * AppRoutes / App). Détecte si l'utilisateur est sur Preview ET sur une URL
 * publique de vérification ; si oui, redirige et bloque le rendu des autres
 * routes. Sinon ne rend rien (transparent).
 *
 * Cette garde couvre automatiquement toutes les routes de vérification, y
 * compris celles ajoutées dans le futur, sans qu'on ait à les wrapper une
 * par une.
 */
const PreviewRedirectGuard = () => {
  const location = useLocation();
  const [shouldRedirect] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (!isPreviewHost(window.location.hostname)) return false;
    return isPublicVerificationPath(window.location.pathname);
  });

  useEffect(() => {
    if (!shouldRedirect) return;
    const target = `${PRODUCTION_URL}${location.pathname}${location.search}${location.hash}`;
    window.location.replace(target);
  }, [shouldRedirect, location.pathname, location.search, location.hash]);

  if (shouldRedirect) return <RedirectingScreen />;
  return null;
};

export default PreviewToProductionRedirect;
export {
  isPreviewHost,
  isPublicVerificationPath,
  PRODUCTION_URL,
  PreviewRedirectGuard,
  PUBLIC_VERIFICATION_PATTERNS,
};
